import { PieChart, PieChartProps } from "@mui/x-charts";
import { alpha, Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import { spacing } from "~/pages/reports/utils/reportsConstants";
import { Flex } from "~/components/Flex";

interface ReportsGroupSummaryProps {
  groupSummary: {
    name: string;
    description: string;
    data: PieChartProps["series"][0]["data"];
  }[];
  totalItems?: number;
}

export function ReportsGroupSummary({
  groupSummary,
  ...props
}: ReportsGroupSummaryProps) {
  const theme = useTheme();
  const totalItems = (data: PieChartProps["series"][0]["data"]) => {
    return data.reduce((acc, item) => acc + item.value, 0);
  };

  return (
    <Grid
      size={12}
      sx={{
        mt: spacing[25],
        "& .group-summary-container:not(:last-child)": { mb: spacing[25] },
      }}
    >
      {groupSummary.map((group) => {
        const chartData = group.data.filter(
          (dataPoint) => dataPoint.id !== "NONE",
        );
        return (
          <Grid
            container
            className="group-summary-container"
            size={12}
            columnSpacing={7}
            rowSpacing={3}
            key={group.name}
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Grid size={{ xs: 12, sm: "grow" }}>
              <Typography fontWeight={700} sx={{ mb: 1, lineHeight: "16px" }}>
                {group.name}
              </Typography>
              <Typography sx={{ lineHeight: "24px" }}>
                {group.description}
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 5 }}
              sx={{
                maxWidth: 300,
              }}
            >
              <Stack key={group.name} direction="row" spacing={2}>
                <Box flexGrow={1} sx={{ position: "relative" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontSize: 24,
                      fontWeight: 700,
                      letterSpacing: ".48px",
                    }}
                  >
                    {props.totalItems || totalItems(chartData)}
                  </Typography>
                  <PieChart
                    series={[
                      {
                        data: chartData,
                        innerRadius: 38,
                      },
                    ]}
                    height={120}
                    margin={{ right: 0 }}
                    hideLegend
                  />
                </Box>

                <Stack
                  direction="column"
                  rowGap={0.5}
                  flexGrow={1}
                  sx={{
                    justifyContent: "center",
                  }}
                  width="35%"
                >
                  {chartData.map((finding) => {
                    const isZero = finding.value === 0;
                    const color = isZero
                      ? theme.palette.text.disabled
                      : finding.color;

                    return (
                      <Flex
                        key={finding.id}
                        alignItems="center"
                        sx={{
                          gap: 0.5,
                          ...(isZero && { color }),
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 50,
                            border: 4,
                            borderColor: color,
                            backgroundColor: color ? alpha(color, 0.5) : "",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "inherit",
                          }}
                        >
                          {finding.id}{" "}
                          <Typography
                            component="span"
                            sx={{ fontSize: "inherit", color: "inherit" }}
                          >
                            {finding.value}
                          </Typography>
                        </Typography>
                      </Flex>
                    );
                  })}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
}
